import { useEffect, useMemo, useState } from 'react'
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer,
} from 'recharts'
import { useI18n } from 'gunsi-auth'
import { useTally } from '../hooks/useTally.jsx'
import { useTallyI18n } from '../i18n/tally.js'
import { CurrencySelect } from '../components/CurrencySelect.jsx'
import { DatePicker } from '../components/DatePicker.jsx'
import { AmountInput } from '../components/AmountInput.jsx'
import { fxConvert, fxHistory } from '../api.js'
import { formatAmount, formatDay } from '../format.js'
import { useIsDesktop } from '../useIsDesktop.js'

function isoToday() {
  const d = new Date()
  const pad = (n) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

export function Converter() {
  const t = useTallyI18n()
  const { locale } = useI18n()
  const { data } = useTally()
  const isDesktop = useIsDesktop()
  const isLil   = data.calendar === 'lil'
  const yearEra = data.yearEra ?? 'lil'

  const [from, setFrom]     = useState(data.currency || 'EUR')
  const [to,   setTo]       = useState('USD')
  const [amount, setAmount] = useState('1')
  const [date, setDate]     = useState(isoToday())
  const [dateOpen, setDateOpen] = useState(false)

  // Last fxConvert response (for the live result panel)
  const [result, setResult]   = useState(null) // { amount, from, to, date, result } | null
  const [loadingR, setLoadR]  = useState(false)
  const [errorR, setErrorR]   = useState(null)

  // Historical series for the currently-selected pair, refetched on
  // (from, to, chartRange) change — not on amount/date — to avoid
  // hammering the backend while typing in the amount field.
  const [history, setHistory] = useState([])
  const [loadingH, setLoadingH] = useState(false)
  const [chartRange, setChartRange] = useState('1Y') // 1W | 1M | 3M | 6M | 1Y | ALL

  // Reload conversion when inputs change
  useEffect(() => {
    const a = parseFloat(amount)
    if (!Number.isFinite(a)) { setResult(null); return }
    setLoadR(true); setErrorR(null)
    fxConvert({ amount: a, from, to, date })
      .then(r => { setResult(r); setLoadR(false) })
      .catch(e => { setResult(null); setErrorR(e.message); setLoadR(false) })
  }, [amount, from, to, date])

  // Fetch history when the pair or selected range changes
  useEffect(() => {
    if (!from || !to || from === to) { setHistory([]); return }
    const daysByRange = { '1W': 7, '1M': 30, '3M': 90, '6M': 180, '1Y': 365, '3Y': 1095, '5Y': 1825 }
    const days = daysByRange[chartRange] // undefined for ALL
    let since = null
    if (days != null) {
      const sinceDate = new Date()
      sinceDate.setUTCDate(sinceDate.getUTCDate() - days)
      const pad = (n) => String(n).padStart(2, '0')
      since = `${sinceDate.getUTCFullYear()}-${pad(sinceDate.getUTCMonth()+1)}-${pad(sinceDate.getUTCDate())}`
    }
    setLoadingH(true)
    fxHistory({ from, to, since })
      .then(r => { setHistory(Array.isArray(r) ? r : (r?.series ?? [])); setLoadingH(false) })
      .catch(() => { setHistory([]); setLoadingH(false) })
  }, [from, to, chartRange])

  function swap() {
    setFrom(to)
    setTo(from)
  }

  return (
    <div className="view">
      <section className="card cv-card">
        {/* Amount: full-width input, no label */}
        <AmountInput value={amount} onChange={setAmount} placeholder={t.converter.amountPlaceholder} />

        {/* From / Swap / To — centred row, no labels */}
        <div className="cv-pair">
          <CurrencySelect
            value={from}
            onChange={setFrom}
            searchPlaceholder={t.common.searchCurrency}
            title={t.converter.from}
            closeLabel={t.common.close}
          />
          <button
            type="button"
            className="cv-swap"
            onClick={swap}
            aria-label={t.converter.swap}
            title={t.converter.swap}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M7 3v18M4 18l3 3 3-3" />
              <path d="M17 21V3M20 6l-3-3-3 3" />
            </svg>
          </button>
          <CurrencySelect
            value={to}
            onChange={setTo}
            searchPlaceholder={t.common.searchCurrency}
            title={t.converter.to}
            closeLabel={t.common.close}
          />
        </div>

        {/* Date: label left, button right */}
        <div className="cv-row">
          <span className="cv-row-label">{t.transactions.date}</span>
          <button
            type="button"
            className="tx-date-btn cv-date-btn"
            onClick={() => setDateOpen(true)}
          >
            {formatDateDisplay(date, locale, data.dateFormat)}
          </button>
        </div>

        {/* Result */}
        <div className="cv-result">
          <span className="cv-result-label">{t.converter.result}</span>
          <span className="cv-result-val" title={errorR || (result?.result == null ? t.converter.noRate : '')}>
            {loadingR
              ? '…'
              : (errorR || result?.result == null)
                ? '—'
                : formatAmount(result.result, to)
            }
          </span>
        </div>
      </section>

      {/* Historical rate chart. Hidden via CSS whenever any sheet/numpad
          is mounted (body.has-sheet) so it doesn't peek out from behind. */}
      <section className="cv-chart-wrap">
        <h3 className="cv-chart-head">{t.converter.historyTitle}</h3>
        {from === to ? (
          <div className="cv-chart-empty">{t.converter.historyEmpty}</div>
        ) : loadingH ? (
          <div className="cv-chart-empty"><div className="loading-spinner" /></div>
        ) : history.length < 2 ? (
          <div className="cv-chart-empty">{t.converter.historyEmpty}</div>
        ) : (
          <FxHistoryChart series={history} from={from} to={to} locale={locale} isDesktop={isDesktop} />
        )}
        {/* Range selector (always visible when the pair is valid, even
            during loading — so the user can change ranges without waiting). */}
        {from !== to && (
          <div className="cv-range-pills" role="tablist">
            {['1W', '1M', '3M', '6M', '1Y', '3Y', '5Y', 'ALL'].map(r => (
              <button
                key={r}
                type="button"
                role="tab"
                aria-selected={chartRange === r}
                className={`cv-range-pill${chartRange === r ? ' active' : ''}`}
                onClick={() => setChartRange(r)}
              >
                {t.converter.ranges[r]}
              </button>
            ))}
          </div>
        )}
      </section>

      <DatePicker
        open={dateOpen}
        value={date}
        onChange={setDate}
        onClose={() => setDateOpen(false)}
        isLilAvailable={isLil}
        yearEra={yearEra}
        locale={locale}
        t={t}
      />
    </div>
  )
}

// ── FX history chart (recharts — same library as gunsi-ledger so it's
//    point-by-point navigable: hover/touch → tooltip with date + rate). ─
function FxHistoryChart({ series, from, to, locale, isDesktop }) {
  // Pre-pick rate-label precision based on the series magnitude so the
  // Y-axis ticks and the tooltip stay readable for very-small (BTC) or
  // large (JPY) rates.
  const fmtRate = useMemo(() => {
    const rates = series.map(p => p.rate)
    const refMag = Math.max(Math.abs(Math.min(...rates)), Math.abs(Math.max(...rates)), 0.001)
    const decimals = refMag < 0.01 ? 6 : refMag < 1 ? 4 : refMag < 100 ? 3 : 2
    return (v) => (v == null ? '—' : Number(v).toFixed(decimals))
  }, [series])

  const fmtDate = (iso) => {
    if (!iso) return ''
    const [y, m, d] = iso.split('-').map(Number)
    const lang = (locale || 'en').startsWith('it') ? 'it' : 'en'
    return new Intl.DateTimeFormat(lang, { day: '2-digit', month: 'short', year: 'numeric' })
      .format(new Date(y, m - 1, d))
  }

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null
    const r = payload[0].value
    return (
      <div className="cv-chart-tooltip">
        <div className="cv-chart-tooltip-date">{fmtDate(label)}</div>
        <div className="cv-chart-tooltip-rate">
          1 {from} = <strong>{fmtRate(r)}</strong> {to}
        </div>
      </div>
    )
  }

  return (
    <ResponsiveContainer width="100%" height={isDesktop ? 220 : 300}>
      <LineChart data={series} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="var(--g-border)" strokeOpacity={0.5} />
        <XAxis
          dataKey="date"
          tickFormatter={iso => fmtDate(iso).replace(/\s+\d{4}$/, '')}
          tick={{ fontSize: 10, fill: 'var(--g-text-muted)' }}
          minTickGap={40}
          axisLine={false}
          tickLine={false}
        />
        <YAxis
          tickFormatter={fmtRate}
          tick={{ fontSize: 10, fill: 'var(--g-text-muted)' }}
          axisLine={false}
          tickLine={false}
          width={64}
          domain={['auto', 'auto']}
        />
        <Tooltip content={<CustomTooltip />} cursor={{ stroke: 'var(--g-border)', strokeWidth: 1 }} />
        <Line
          type="monotone"
          dataKey="rate"
          stroke="var(--g-accent)"
          strokeWidth={2}
          dot={false}
          activeDot={{ r: 4, fill: 'var(--g-accent)' }}
          isAnimationActive={false}
        />
      </LineChart>
    </ResponsiveContainer>
  )
}

function formatDateDisplay(iso, locale, dateFormat) {
  if (!iso) return '—'
  return formatDay(iso, dateFormat, locale)
}
